import {
  SHAutocomplete,
  SHBox,
  SHCheckbox,
  SHChip,
  SHRadio,
  SHStack,
  SHTextFieldProps,
  SHTypography,
} from "@components/design-systems";
import { ProductLOV } from "@models/product/entities/product";
import { InformationButton } from "@components/buttons/information";
import { useTheme } from "@mui/material";
import { useMemo } from "react";

type SelectProductsV2Props = {
  options?: ProductLOV[];
  maxLength?: number;
  textFieldProps?: Omit<SHTextFieldProps, "value">;
  multiple?: boolean;
  selectedIds?: string[] | string | null;
  disabled?: boolean;
  badgeText?: string;

  onChange: (value: string[] | string | null) => void;
};

export const SelectProductsV2 = ({
  options = [],
  maxLength = options?.length,
  multiple = false,
  textFieldProps,
  selectedIds = [],
  disabled = false,
  badgeText = "Main",
  onChange,
}: SelectProductsV2Props) => {
  const { palette } = useTheme();

  const compiledValue: ProductLOV | ProductLOV[] | null = useMemo(() => {
    if (selectedIds == null) return null;

    const ids = Array.isArray(selectedIds) ? selectedIds : [selectedIds];

    if (!multiple) return options?.find((o) => o.id === ids[0]) ?? null;

    return ids
      .map((id) => options?.find((o) => o.id === id))
      .filter(Boolean) as ProductLOV[];
  }, [selectedIds, multiple, options]);

  const selectedProductsArray: ProductLOV[] | null = useMemo(() => {
    if (!compiledValue) return null;
    return Array.isArray(compiledValue) ? compiledValue : [compiledValue];
  }, [compiledValue]);

  return (
    <SHStack spacing={"16px"} direction="column">
      <SHAutocomplete
        multiple={multiple}
        value={compiledValue}
        options={options}
        disabled={disabled}
        disableCloseOnSelect
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        getOptionLabel={(option: ProductLOV) => option.name}
        textFieldProps={{
          ...textFieldProps,
          InputLabelProps: { shrink: true },
          InputProps: {
            startAdornment:
              selectedProductsArray?.length && multiple ? (
                <SHTypography
                  disabled={disabled}
                  variant="body1"
                  sx={{ paddingLeft: "10px" }}
                >
                  {`${selectedProductsArray?.length}/${maxLength} selected`}
                </SHTypography>
              ) : undefined,
          },
        }}
        onChange={(_, newValue) => {
          if (!multiple) {
            onChange(!Array.isArray(newValue) ? newValue?.id ?? null : null);
            return;
          }

          if (!Array.isArray(newValue)) {
            onChange([]);
            return;
          }

          if (maxLength && newValue?.length > maxLength) return;

          const newProducts = newValue
            .filter((option) => !option.isMain)
            .map((option) => option.id);
          onChange(newProducts);
        }}
        renderOption={(props, option, { selected }) => {
          const isMainProduct = !!option.isMain;
          const isChecked = selected || isMainProduct;

          return (
            <li
              {...props}
              onClick={(e) => {
                if (isMainProduct) return;
                props.onClick?.(e);
              }}
              key={option.id}
              style={{ padding: "1px 10px" }}
            >
              <SHStack alignItems={"center"} direction="row" spacing={1}>
                {multiple ? (
                  <SHCheckbox checked={isChecked} disabled={isMainProduct} />
                ) : (
                  <SHRadio disabled={isMainProduct} checked={isChecked} />
                )}
                <SHTypography
                  variant="body3"
                  disabled={isMainProduct}
                  fontWeight={500}
                >
                  {option.name}
                </SHTypography>
                {option.description && (
                  <InformationButton content={option.description} />
                )}
                {isMainProduct && (
                  <SHChip
                    sx={{ ml: "25px !important" }}
                    color="warning"
                    size="small"
                    label={badgeText}
                    variant="outlined"
                  />
                )}
              </SHStack>
            </li>
          );
        }}
      />
      {multiple && (
        <SHBox
          sx={{
            height: "100px",
            px: "12px",
            py: "10px",
            border: `1px solid ${palette.divider}`,
            overflowY: "auto",
          }}
          p={"12px"}
        >
          <SHTypography variant="body2">
            {selectedProductsArray?.map((option) => option.name)?.join(", ")}
          </SHTypography>
        </SHBox>
      )}
    </SHStack>
  );
};
